import React, { 
  useEffect, useMemo, useState
} from 'react';

import { ClockIcon, DownloadIcon } from '@primer/octicons-react';
import { StyledOcticon, Timeline } from '@primer/react';

import { formatISOAsRelative } from '../../utils/date_utils';
import { truncateText } from '../../utils/text_utils';

import ClipboardLabel from '../common/ClipboardLabel/ClipboardLabel';
import CodeFormatLabel from '../common/Labels/CodeFormatLabel';
import DividerDot from '../common/misc/DividerDot';
import ErrorLabel from '../common/Labels/ErrorLabel';
import Label from '../common/Labels/Label';
import LinkLabel from '../common/Labels/LinkLabel';
import LoadingSpinner from '../common/LoadingSpinner/LoadingSpinner';

import PublishStateIcons from './PublishStateIcons';
import PublishStatusMessage from './PublishStatusMessage';

import { preserveWorkspaceURL } from '../../utils/url_utils';

export const LoadingVersionItem = ({ condensed }) => (
  <Timeline.Item condensed={condensed}>
    <Timeline.Badge>
      <StyledOcticon icon={DownloadIcon} />
    </Timeline.Badge>
    <Timeline.Body>
      <div className="p-1">
        <LoadingSpinner animationKey="version-item-loading-spinner" />
      </div>
    </Timeline.Body>
  </Timeline.Item>
)

const ModelVersionItem = ({
  modelID,
  modelVersion,
  modelSHA,
  fileName,
  createdAt,
  condensed = false,
}) => {
  const truncatedSha = useMemo(() => truncateText(modelSHA, 16), [modelSHA]);

  return (
    <Timeline.Item className="text-brand items-center" condensed={condensed}>
      <Timeline.Badge>
        <StyledOcticon icon={ClockIcon} />
      </Timeline.Badge>
      <Timeline.Body>
        <div className="p-1 flex justify-between items-center">
          <div className="flex flex-col">
            <LinkLabel
              to={preserveWorkspaceURL(`/models/${modelID}/versions/${modelVersion}`)}
              bold
            >
              {modelVersion}
            </LinkLabel>
            {
              createdAt && (
                <ErrorLabel className="ml-1" size="sm">created {formatISOAsRelative(createdAt)}</ErrorLabel>
              )
            }
          </div>
          <div className="flex items-center">
            <Label className="mr-1">
              {fileName}
            </Label>
            <DividerDot />
            <ClipboardLabel
              className="p-1"
              iconSize={16}
              labelValue={modelSHA}
              displayLabel={truncatedSha}
              LabelComponent={CodeFormatLabel}
              showClipboardIconOnHover
            />
          </div>
        </div>
      </Timeline.Body>
    </Timeline.Item>
  );
}

const PipelineVersionItem = ({
  pipelineIdName,
  pipelineVersion,
  createdAt,
  engineConfig,
  modelConfigs,
  pipelineVersionId,
  isEdgePublishingEnabled = false,
  condensed = false,
  listOfPublishesForPipeline,
  handlePipelinePublishStatusFetch,
  publishRequest,
  setNewPublish
}) => {
  const modelConfigIdsSet = new Set(modelConfigs.map(config => config.model_config_id))
  const [publishStatus, setPublishStatus] = useState(null)
  const [data, setData] = useState(null)
 
  useEffect(() => {
    if (!isEdgePublishingEnabled) {
      return;
    }
    const publishByPipelineId = listOfPublishesForPipeline?.filter(publishData => publishData?.pipeline_version_id === pipelineVersionId)

    const containsNewPublish = listOfPublishesForPipeline?.filter(publishData => publishData?.status !== 'Published' || publishData?.status !== 'Error')

    if (publishByPipelineId) {
      const publishDataList = publishByPipelineId?.filter(d => pipelineVersionId === d?.pipeline_version_id)
      const latestPublishData = publishDataList[publishDataList.length - 1]
      
      setPublishStatus(latestPublishData?.status)
      setData(latestPublishData)
    }

    if (containsNewPublish?.length === 0) {
      setNewPublish(false)
    }
  }, [isEdgePublishingEnabled, listOfPublishesForPipeline, pipelineVersionId, setNewPublish])

  return (
    <Timeline.Item className="text-brand items-center" condensed={condensed}>
      <Timeline.Badge >
        <StyledOcticon icon={ClockIcon} />
      </Timeline.Badge>
      <Timeline.Body>
        <div className="p-1 flex justify-between items-center">
          <div className="flex">
            <div>
              <LinkLabel
                to={preserveWorkspaceURL(`/pipelines/${pipelineIdName}/versions/${pipelineVersion}`)}
                bold
              >
                {pipelineVersion}
              </LinkLabel>
              {
                isEdgePublishingEnabled && (
                  <PublishStatusMessage
                    handlePipelinePublishStatusFetch={handlePipelinePublishStatusFetch}
                    status={publishStatus}
                    data={data}
                  />
                )
              }
            </div>
            {
              isEdgePublishingEnabled && (
                <PublishStateIcons
                  engineConfig={engineConfig}
                  modelConfigIds={[...modelConfigIdsSet]}
                  pipelineVersionId={pipelineVersionId}
                  status={publishStatus}
                  publishRequest={publishRequest}
                />
              )
            }
          </div>
          {
            createdAt && (
              <ErrorLabel className="ml-1" size="sm">created {formatISOAsRelative(createdAt)}</ErrorLabel>
            )
          }
        </div>
      </Timeline.Body>
    </Timeline.Item>
  )
};

export const VersionHistoryItem = ({
  entry,
  modelID,
  pipelineIdName,
  engineConfig,
  modelConfigs,
  isEdgePublishingEnabled = false,
  condensed = false,
  listOfPublishesForPipeline,
  handlePipelinePublishStatusFetch,
  publishRequest,
  setNewPublish
}) => {
  if (modelID) {
    return (
      <ModelVersionItem
        modelID={modelID}
        modelVersion={entry?.modelVersion}
        modelSHA={entry?.sha}
        fileName={entry?.fileName}
        createdAt={entry?.createdAt}
        condensed={condensed}
      />
    );
  }
  if (pipelineIdName) {
    return (
      <PipelineVersionItem
        pipelineIdName={pipelineIdName}
        pipelineVersion={entry?.version}
        pipelineVersionId={entry?.id}
        createdAt={entry?.createdAt}
        condensed={condensed}
        engineConfig={engineConfig}
        modelConfigs={modelConfigs}
        listOfPublishesForPipeline={listOfPublishesForPipeline}
        handlePipelinePublishStatusFetch={handlePipelinePublishStatusFetch}
        publishRequest={publishRequest}
        setNewPublish={setNewPublish}
        isEdgePublishingEnabled={isEdgePublishingEnabled}
      />
    );
  }

  return <Timeline.Break />
}

export default VersionHistoryItem;
